import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import clsx from "clsx";

interface LoaderProps {
    onLoadingComplete?: () => void;
    className?: string;
}

export default function Loader({ onLoadingComplete, className }: LoaderProps) {
    const [complete, setComplete] = useState(false);
    const [textIndex, setTextIndex] = useState(0);

    const texts = ["INNOVATION", "TECHNOLOGY", "FUTURE", "SAN micron"];

    useEffect(() => {
        // Text animation sequence
        const textInterval = setInterval(() => {
            setTextIndex((prev) => {
                if (prev < texts.length - 1) return prev + 1;

                clearInterval(textInterval);
                return prev;
            });
        }, 500); // Change text every 500ms

        // Complete loading after a set time (e.g., 2.5s)
        const timeout = setTimeout(() => {
            setComplete(true);
            if (onLoadingComplete) {
                setTimeout(onLoadingComplete, 1000); // Allow exit animation to finish
            }
        }, 2500);

        return () => {
            clearTimeout(timeout);
            clearInterval(textInterval);
        };
    }, [onLoadingComplete, texts.length]);

    return (
        <motion.div
            initial={{ y: 0 }}
            animate={{ y: complete ? "-100%" : 0 }}
            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
            className={clsx(
                "fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#000000e0] text-white",
                className
            )}
        >
            {/* Abstract Background pattern */}
            <div className="absolute inset-0 opacity-20 pointer-events-none">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-indigo-900/40 via-[#000000e0] to-[#000000e0]"></div>
            </div>

            <div className="relative z-10 flex flex-col items-center">
                <motion.div
                    key={textIndex}
                    initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, y: -20, filter: 'blur(10px)' }}
                    transition={{ duration: 0.4 }}
                    className="text-4xl md:text-6xl font-bold tracking-wider mb-8 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-600"
                >
                    {texts[textIndex]}
                </motion.div>

                {/* Progress Bar */}
                <div className="w-64 h-1 bg-gray-800 rounded-full overflow-hidden mt-4">
                    <motion.div
                        className="h-full bg-gradient-to-r from-blue-500 to-purple-600"
                        initial={{ width: "0%" }}
                        animate={{ width: complete ? "100%" : `${(textIndex + 1) * (100 / texts.length)}%` }}
                        transition={{ duration: 0.5 }}
                    />
                </div>
            </div>
        </motion.div>
    );
}
